import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AudioRecorderProps {
	onRecordingComplete: (blob: Blob) => void;
	onReset?: () => void;
	autoStart?: boolean;
}

type RecorderState = "idle" | "recording" | "recorded";

// ── Constants ─────────────────────────────────────────────────────────────────

const SAMPLE_COUNT = 800;
const BAR_COUNT = 80;
const WAVE_SCALE_FACTOR = 9;

// ── Drawing ───────────────────────────────────────────────────────────────────

function drawBars(canvas: HTMLCanvasElement, samples: Float32Array): void {
	const ctx = canvas.getContext("2d");
	if (!ctx) return;
	const W = canvas.width;
	const H = canvas.height;

	ctx.clearRect(0, 0, W, H);

	// Compute per-bar amplitude (max of samples in that range)
	const samplesPerBar = SAMPLE_COUNT / BAR_COUNT;
	const amps: number[] = [];
	for (let i = 0; i < BAR_COUNT; i++) {
		let max = 0;
		const start = Math.floor(i * samplesPerBar);
		const end = Math.min(SAMPLE_COUNT, Math.floor((i + 1) * samplesPerBar));
		for (let j = start; j < end; j++) {
			if ((samples[j] ?? 0) > max) max = samples[j] ?? 0;
		}
		amps.push(max);
	}

	const gap = 3;
	const barW = (W - gap * (BAR_COUNT - 1)) / BAR_COUNT;
	const maxBarH = H * 0.84;
	const minBarH = Math.max(4, H * 0.045);
	const centerY = H / 2;
	const radius = Math.min(barW / 2, 5);

	function fillBars(offsetX: number) {
		for (let i = 0; i < BAR_COUNT; i++) {
			const amp = amps[i] ?? 0;
			const barH = minBarH + amp * (maxBarH - minBarH);
			const x = i * (barW + gap) + offsetX;
			ctx.beginPath();
			ctx.roundRect(x, centerY - barH / 2, barW, barH, radius);
			ctx.fill();
		}
	}

	// Blue risograph bleed (shifted left)
	ctx.save();
	ctx.globalAlpha = 0.22;
	ctx.fillStyle = "#4a7de8";
	fillBars(-3);
	ctx.restore();

	// Coral risograph bleed (shifted right)
	ctx.save();
	ctx.globalAlpha = 0.14;
	ctx.fillStyle = "#e84b6a";
	fillBars(2.5);
	ctx.restore();

	// Main ink bars
	ctx.save();
	ctx.globalAlpha = 0.82;
	ctx.fillStyle = "#1a1209";
	fillBars(0);
	ctx.restore();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
	const m = Math.floor(seconds / 60)
		.toString()
		.padStart(2, "0");
	const s = (seconds % 60).toString().padStart(2, "0");
	return `${m}:${s}`;
}

function detectMimeType(): string {
	for (const mime of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
		if (MediaRecorder.isTypeSupported(mime)) return mime;
	}
	return "";
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AudioRecorder({
	onRecordingComplete,
	onReset,
	autoStart = false,
}: AudioRecorderProps) {
	const { t } = useTranslation();
	const [recorderState, setRecorderState] = useState<RecorderState>("idle");
	const [duration, setDuration] = useState(0);
	const [error, setError] = useState<string | null>(null);

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const audioCtxRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const animFrameRef = useRef<number | null>(null);
	const samplesRef = useRef<Float32Array>(
		new Float32Array(SAMPLE_COUNT).fill(0),
	);
	const chunksRef = useRef<Blob[]>([]);
	const streamRef = useRef<MediaStream | null>(null);

	// ── Canvas sizing ────────────────────────────────────────────────────────────

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const dpr = window.devicePixelRatio || 1;
		const resize = () => {
			const rect = canvas.getBoundingClientRect();
			if (rect.width === 0) return;
			canvas.width = rect.width * dpr;
			canvas.height = rect.height * dpr;
			drawBars(canvas, samplesRef.current);
		};
		resize();
		const ro = new ResizeObserver(resize);
		ro.observe(canvas);
		return () => ro.disconnect();
	}, []);

	// ── Cleanup on unmount ──────────────────────────────────────────────────────

	useEffect(() => {
		return () => {
			if (animFrameRef.current !== null)
				cancelAnimationFrame(animFrameRef.current);
			streamRef.current?.getTracks().forEach((t) => {
				t.stop();
			});
			audioCtxRef.current?.close();
			audioCtxRef.current = null;
		};
	}, []);

	// ── Timer ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		if (recorderState !== "recording") return;
		const id = setInterval(() => setDuration((d) => d + 1), 1000);
		return () => clearInterval(id);
	}, [recorderState]);

	// ── Live mic waveform loop ──────────────────────────────────────────────────

	const startWaveformLoop = useCallback((analyser: AnalyserNode) => {
		const timeData = new Uint8Array(analyser.fftSize);

		function tick() {
			analyser.getByteTimeDomainData(timeData);
			let sum = 0;
			for (let i = 0; i < timeData.length; i++) {
				const v = (timeData[i] ?? 0) / 128.0 - 1.0; // normalize to -1..1
				sum += v * v;
			}
			const amplitude = Math.min(
				1,
				Math.sqrt(sum / timeData.length) * WAVE_SCALE_FACTOR,
			);

			samplesRef.current.copyWithin(0, 1);
			samplesRef.current[SAMPLE_COUNT - 1] = amplitude;

			const canvas = canvasRef.current;
			if (canvas) drawBars(canvas, samplesRef.current);
			animFrameRef.current = requestAnimationFrame(tick);
		}
		animFrameRef.current = requestAnimationFrame(tick);
	}, []);

	// ── Start recording ─────────────────────────────────────────────────────────

	const startRecording = useCallback(async () => {
		setError(null);
		samplesRef.current = new Float32Array(SAMPLE_COUNT).fill(0);
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			streamRef.current = stream;

			const audioCtx = new AudioContext();
			const source = audioCtx.createMediaStreamSource(stream);
			const analyser = audioCtx.createAnalyser();
			analyser.fftSize = 1024;
			analyser.smoothingTimeConstant = 0;
			source.connect(analyser);
			audioCtxRef.current = audioCtx;
			analyserRef.current = analyser;

			const mimeType = detectMimeType();
			const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
			mediaRecorderRef.current = recorder;
			chunksRef.current = [];

			recorder.ondataavailable = (e: BlobEvent) => {
				if (e.data.size > 0) chunksRef.current.push(e.data);
			};
			recorder.onstop = () => {
				const finalBlob = new Blob(chunksRef.current, {
					type: mimeType || "audio/webm",
				});
				onRecordingComplete(finalBlob);
				setRecorderState("recorded");
			};

			recorder.start(100);
			setRecorderState("recording");
			setDuration(0);
			startWaveformLoop(analyser);
		} catch {
			setError(t("audio.micDenied"));
		}
	}, [onRecordingComplete, startWaveformLoop, t]);

	// ── Auto-start ──────────────────────────────────────────────────────────────

	useEffect(() => {
		if (autoStart) startRecording();
	}, [autoStart, startRecording]);

	// ── Stop recording ──────────────────────────────────────────────────────────

	const stopRecording = useCallback(() => {
		mediaRecorderRef.current?.stop();
		streamRef.current?.getTracks().forEach((t) => {
			t.stop();
		});
		if (animFrameRef.current !== null) {
			cancelAnimationFrame(animFrameRef.current);
			animFrameRef.current = null;
		}
		audioCtxRef.current?.close();
		audioCtxRef.current = null;
	}, []);

	// ── Reset ───────────────────────────────────────────────────────────────────

	const handleReset = useCallback(() => {
		samplesRef.current = new Float32Array(SAMPLE_COUNT).fill(0);
		const canvas = canvasRef.current;
		if (canvas) drawBars(canvas, samplesRef.current);
		setDuration(0);
		setRecorderState("idle");
		onReset?.();
		startRecording();
	}, [onReset, startRecording]);

	// ── Render ──────────────────────────────────────────────────────────────────

	const isActive = recorderState !== "idle";

	return (
		<div className="w-full">
			{/* Row 1: button + waveform, both 64px tall */}
			<div className="relative w-full" style={{ height: "64px" }}>
				{/* Button + label — centered when idle, slides to left when active */}
				<div
					className="absolute top-1/2 z-10"
					style={{
						left: isActive ? "0" : "50%",
						transform: isActive ? "translateY(-50%)" : "translate(-50%, -50%)",
						transition:
							"left 0.45s cubic-bezier(0.4,0,0.2,1), transform 0.45s cubic-bezier(0.4,0,0.2,1)",
					}}
				>
					<button
						type="button"
						onClick={
							recorderState === "idle"
								? startRecording
								: recorderState === "recording"
									? stopRecording
									: handleReset
						}
						aria-label={
							recorderState === "idle"
								? t("audio.startRecording")
								: recorderState === "recording"
									? t("audio.stopRecording")
									: t("audio.reRecord")
						}
						className={`w-16 h-16 rounded-full border-2 border-ink flex items-center justify-center transition-colors ${
							recorderState === "recording"
								? "animate-breathe"
								: "hover:bg-ink/5 active:scale-95"
						}`}
					>
						{recorderState === "recording" ? (
							<span
								className="w-4 h-4 rounded-[3px]"
								style={{ background: "#c0394f" }}
							/>
						) : (
							<span
								className="w-6 h-6 rounded-full"
								style={{ background: "#c0394f" }}
							/>
						)}
					</button>
				</div>

				{/* Waveform — wipes in from left, matches button height exactly */}
				<div
					className="absolute top-0 bottom-0 right-0"
					style={{
						left: "80px",
						clipPath: isActive ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
						opacity: isActive ? 1 : 0,
						transition:
							"clip-path 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease-out",
					}}
				>
					<canvas
						ref={canvasRef}
						className="w-full h-full"
						style={{
							opacity: recorderState === "recorded" ? 0.5 : 1,
							transition: "opacity 0.3s ease-out",
						}}
					/>
				</div>
			</div>

			{/* Row 2: labels — below button and waveform respectively */}
			<div className="flex items-center mt-2">
				{/* Under button (64px wide) */}
				<div
					className="flex-shrink-0 flex justify-center transition-all duration-[450ms] cubic-bezier(0.4,0,0.2,1)"
					style={{ width: isActive ? "64px" : "100%" }}
				>
					<p className="font-ui text-[10px] uppercase tracking-widest text-ink/30 whitespace-nowrap">
						{recorderState === "idle"
							? t("audio.tapToRecord")
							: recorderState === "recording"
								? t("audio.tapToStop")
								: t("audio.reRecord")}
					</p>
				</div>

				{/* Under waveform — fades in with gap matching the 16px between button and waveform */}
				<div
					className="flex-1 flex items-center justify-between overflow-hidden"
					style={{
						paddingLeft: "16px",
						opacity: isActive ? 1 : 0,
						transition: "opacity 0.4s ease-out",
					}}
				>
					{recorderState === "recording" && (
						<p className="font-ui text-xs text-ink/40 tracking-[0.2em] tabular-nums ml-auto">
							{formatDuration(duration)}
						</p>
					)}
					{recorderState === "recorded" && (
						<>
							<span className="font-ui text-[10px] uppercase tracking-widest text-teal">
								{t("audio.ready")}
							</span>
							<p className="font-ui text-xs text-ink/40 tracking-[0.2em] tabular-nums">
								{formatDuration(duration)}
							</p>
						</>
					)}
				</div>
			</div>

			{error && (
				<p className="font-ui text-[10px] uppercase tracking-wide text-coral/80 text-center max-w-xs mt-3">
					{error}
				</p>
			)}
		</div>
	);
}
